import config from 'constants/config'
import { BRAINTREE_PAYMENT_TYPE, VELOCITY_EARN_INELIGIBLE_LOCATIONS, paymentMethodLabels } from 'constants/payment'
import getPaymentMethods from './getPaymentMethods'
import { isAfterPayBpEnabled, isWithinAfterPayBpAvailabilityRange } from 'lib/afterPayBpUtils'
import { isHoolahBpEnabled, isWithinHoolahBpAvailabilityRange } from 'lib/hoolahBpUtils'
import { isAtomeBpEnabled, isWithinAtomeBpAvailabilityRange } from 'lib/atomeBpUtils'
import { isWaaveBpEnabled, isWithinWaaveBpAvailabilityRange } from 'lib/waaveBpUtils'
import { isKlarnaBpEnabled, isWithinKlarnaBpAvailabilityRange } from 'lib/klarnaBpUtils'
import { isLatitudePayEnabled } from 'lib/latitudeUtils'
import { isKlarnaEnabled, isWithinKlarnaAvailabilityRange } from 'lib/klarnaUtils'
import { FLASH_HOTELS_AND_TAO_OFFERS_TYPES } from 'constants/offerList'

export function calculateInstalments(amount: number, instalments: number): number {
  return Math.ceil(parseFloat(((amount / instalments) * 100).toFixed(3))) / 100
}

export function isPaypalEnabled(currency: string) {
  return config.PAYPAL_ENABLED && getPaymentMethods(currency).includes(BRAINTREE_PAYMENT_TYPE)
}

export function getBNPLMethodAvailability(currencyCode: string, ...prices: Array<number>) {
  return {
    isKlarnaAvailable: isKlarnaEnabled(currencyCode) && prices.some(price => isWithinKlarnaAvailabilityRange(price)),
    isKlarnaBpAvailable: isKlarnaBpEnabled(currencyCode) && prices.some(price => isWithinKlarnaBpAvailabilityRange(price, currencyCode)),
    isLatitudePayEnabled: isLatitudePayEnabled(currencyCode),
    isAfterPayBpAvailable: isAfterPayBpEnabled(currencyCode) && prices.some(price => isWithinAfterPayBpAvailabilityRange(price, currencyCode)),
    isHoolahBpAvailable: isHoolahBpEnabled(currencyCode) && prices.some(price => isWithinHoolahBpAvailabilityRange(price, currencyCode)),
    isAtomeBpAvailable: isAtomeBpEnabled(currencyCode) && prices.some(price => isWithinAtomeBpAvailabilityRange(price, currencyCode)),
    isWaaveBpAvailable: isWaaveBpEnabled(currencyCode) && prices.some(price => isWithinWaaveBpAvailabilityRange(price, currencyCode)),
  }
}

export function isWithInStartDateRange(startDate, eligibilityRangeInDays) {
  if (!startDate) return true

  const currentDate = Date.now()
  const eligibilityRange = eligibilityRangeInDays * 24 * 60 * 60 * 1000

  const startDateInIsoFormat = new Date(startDate).toISOString()
  const startDateInMiliseonds = Date.parse(startDateInIsoFormat)

  return currentDate + eligibilityRange < startDateInMiliseonds
}

export function paymentMethodsToString(paymentMethods: Array<string> = []): string {
  const s = new Set<string>()
  paymentMethods.forEach(method => s.add(paymentMethodLabels[method] || method))
  const arr = Array.from(s)
  const formatter = new Intl.ListFormat('en', { type: 'disjunction' })
  return `This promo code applies to ${formatter.format(arr)}`
}

export function isCardAcceptedMerchantFeePaymentType(brand: string): brand is App.MerchantFeePaymentType {
  const merchantFeePaymentTypes: App.MerchantFeePaymentType[] = ['visa', 'mastercard', 'amex']
  return merchantFeePaymentTypes.includes(brand as App.MerchantFeePaymentType)
}

export function calculateMerchantFeeForOrder(merchantFeeDetails: App.MerchantFeeDetails[]): number {
  if (!merchantFeeDetails) return 0
  return merchantFeeDetails.reduce((total, merchantFeeItem) => {
    return total + parseFloat(merchantFeeItem.amount)
  }, 0)
}

export function getMerchantFeeByOrderItem(merchantFeeDetails: App.MerchantFeeDetails[], itemId: string): App.MerchantFeeDetails | null {
  const merchantFeeItem = merchantFeeDetails?.find(item => item.fk_item === itemId)
  return merchantFeeItem || null
}

export function isVelocityEarnEnabled(offer) : boolean {
  if (!offer) return true
  if (!offer.locations) {
    offer.locations = [offer.property?.locationSubheading] // this will usually be the city
    offer.locations.push(offer.property?.address?.split(', ').pop()) // this will usually be the country
  }
  offer.locations = offer.locations.filter(location => !!location)
  if (
    (FLASH_HOTELS_AND_TAO_OFFERS_TYPES.includes(offer.type) || FLASH_HOTELS_AND_TAO_OFFERS_TYPES.includes(offer.productType)) &&
    (VELOCITY_EARN_INELIGIBLE_LOCATIONS.some(location => offer.locations.length && offer.locations.map(loc => loc.toLowerCase()).includes(location)))
  )
  {
    return config.VIRGIN_VELOCITY_EARN_ENABLED
  } else {
    return true
  }
}
